/* eslint-disable @typescript-eslint/no-explicit-any */
import { CircularProgress } from "@mui/material";
import React, { lazy, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegHeart } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import { HiOutlineBookOpen } from "react-icons/hi";
import { MdHeadset } from "react-icons/md";
import { PiShareFat } from "react-icons/pi";
import { HeadersFunction, MetaFunction, useLoaderData, useParams } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";

import NodataFound from "~/components/shared-components/NoDataFound";
import { useFontStyle } from "~/hook/useFontStyle";
import useNavigateAudioBook from "~/hook/useNavigateAudioBook";
import { BookDetailType } from "~/interface/sad-granth-types/book";
import { BookListType } from "~/interface/sad-granth-types/book-list";
import { useAnalytics } from "~/lib/firebase/analytics";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";

import ImageWithSkeleton from "~/components/shared-components/ImageWithSkeleton";
import { cn } from "~/lib/utils";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, downloadFile } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

const DisplayBookCard = lazy(() => import("~/components/shared-components/DisplayBookCard"));

type LoaderData = {
  books: BookDetailType;
  title: string;
  writer?: string;
  audio?: string;
  bookList: BookListType[];
  bookPart: number;
  og_image?: string;
  og_width?: string;
  og_height?: string;
};

export const meta: MetaFunction = ({ location, data, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const metaData = data as LoaderData;
  const meta = getPageMeta("sadGranth", lang);

  return metaService({
    title: metaData?.books.book?.meta_title || metaData?.books.book?.name || meta.metaTitle,
    description: metaData?.books.book?.meta_desc || metaData?.books.book?.desc || meta.metaDescription,
    ogTitle: metaData?.books.book?.meta_title || metaData?.books.book?.name || meta.ogTitle,
    ogDescription: metaData?.books.book?.meta_desc || metaData?.books.book?.desc || meta.ogDescription,
    ogUrl: pathname,
    ogImg: metaData?.og_image || meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: metaData?.og_width || meta.ogImageWidth || "1200",
    ogHeight: metaData?.og_height || meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string; slug: string; part: string }; request: Request }): Promise<any> => {
  const { lang, slug, part } = params;
  const bookPartNumber = +part?.split("-")[1] || 1;

  const apiService = new ApiService(lang || LANG.english);

  // get book info from api
  const BookInfoDataBaseOnSlug: BookDetailType = await apiService.getSadGranthBaseOnSlug(slug);
  if (BookInfoDataBaseOnSlug.book === null) {
    throw new Response("No Data Found", { status: 400 });
  }
  // get mst data for author and audio by
  const mstResponse = await apiService.getMasterData();

  const personData: MstPersonType[] = mstResponse?.book_author;
  const authorName = personData?.find((item) => item.id === BookInfoDataBaseOnSlug?.book.author_id)?.text;
  const audioBy = personData?.find((item) => item.id === BookInfoDataBaseOnSlug?.book.audio_by)?.text;

  // get more books data from api
  const moreBookResponse = await apiService.getSadGranth();
  const DATA = {
    books: BookInfoDataBaseOnSlug,
    title: BookInfoDataBaseOnSlug?.book.name,
    og_image: BookInfoDataBaseOnSlug?.book?.img?.og?.url || FALLBACK_IMAGE_URL,
    og_width: BookInfoDataBaseOnSlug?.book?.img?.og?.w.toString() || "1200",
    og_height: BookInfoDataBaseOnSlug?.book?.img?.og?.h.toString() || "630",
    writer: authorName,
    audio: audioBy,
    bookList: moreBookResponse,
    bookPart: bookPartNumber,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.SAD_GRANTH.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

const SadGranthBaseOnId = () => {
  const { t } = useTranslation();
  const { mediumInter, semiBold, medium } = useFontStyle();
  const lang = useParams().lang as "en" | "gu";
  const { isLoading: isAudioNavigateLoading, handleNavigateAudioBook } = useNavigateAudioBook(lang);
  const { trackDownload } = useAnalytics();

  const [isLoading, setIsLoading] = useState(false);

  const { books, audio, writer, title, bookList, bookPart } = useLoaderData<LoaderData>();
  const { book, book_part } = books;
  const book_part_data = book_part?.find((item) => item.part_number === bookPart) || book_part[0];
  const sameTypeBooks = bookList?.filter((item) => item.type === book.type && item.id !== book.id);
  const nonMatchTypeBooks = bookList?.filter((item) => item.id !== book.id);
  const sadGranthData = sameTypeBooks && sameTypeBooks.length > 0 ? sameTypeBooks.slice(0, 12) : nonMatchTypeBooks?.slice(0, 6);

  const handleDownloadFile = async (e: React.MouseEvent, pdf_path: string, fileName: string) => {
    e.preventDefault();
    e.stopPropagation();

    setIsLoading(true);
    try {
      await downloadFile(pdf_path, `${fileName}.pdf`);

      // Track the download event
      trackDownload(`${fileName}.pdf`, "pdf", "SadGranthBookPage");
    } catch (error) {
      // Handle error if needed
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayAudio = async (playlistId: number | null) => {
    if (!playlistId) return;
    await handleNavigateAudioBook(playlistId);
  };

  const BreadCurb = () => (
    <BreadCrubComponent
      breadCrub={[
        {
          path: `/sad-granth`,
          title: t("sad_granth.title"),
        },
        book.n_part > 1 && bookPart > 1
          ? {
              path: `/sad-granth/${book.url_slug}`,
              title: title || "",
            }
          : null,
      ].filter((item) => item !== null)}
      title={
        book.n_part > 1 && bookPart > 1
          ? `${t(`sad_granth.part`)} - ${bookPart}`
          : book.n_part > 1
            ? `${title.replaceAll("-", " ")} ( ${t(`sad_granth.part`)} - ${bookPart} )` || ""
            : title.replaceAll("-", " ") || ""
      }
    />
  );
  return (
    <div className="mx-auto px-4 pb-8">
      <div className="xs:px-2 mb-5 md:mb-5 w-full flex items-center justify-start">
        <BreadCurb />
      </div>
      <div className="w-full flex flex-col lg:flex-row gap-1 md:gap-10 h-full">
        <div className="w-[50%] mx-auto md:w-[30%] aspect-[2/3] flex items-center justify-center lg:bg-cardBg md:pb-5 md:px-5 lg:py-10 rounded-lg">
          <ImageWithSkeleton
            url={{
              small: book_part_data?.img?.s?.url ?? book_part_data?.thumb_img ?? FALLBACK_IMAGE_URL,
              medium: book_part_data?.img?.m?.url ?? book_part_data?.thumb_img ?? FALLBACK_IMAGE_URL,
              large: book_part_data?.img?.l?.url ?? book_part_data?.thumb_img ?? FALLBACK_IMAGE_URL,
              hd: book_part_data?.img?.hd?.url ?? book_part_data?.thumb_img ?? FALLBACK_IMAGE_URL,
              original: book_part_data?.img?.og?.url ?? book_part_data?.thumb_img ?? FALLBACK_IMAGE_URL,
            }}
            blur_hash={book_part_data?.img?.bh}
            alt={book.meta_title || book.name.replaceAll("-", " ")}
            isPreloaded={true}
          />
        </div>
        {/* download and read book and info card section */}
        <div className="w-full lg:w-[70%] md:px-3 flex flex-col-reverse md:flex-col ">
          <div className="hidden md:flex flex-col-reverse lg:flex-row justify-between gap-5">
            <p className={cn("text-textColor text-xl md:text-4xl first-letter-cap", semiBold)}>
              {book.name.replaceAll("-", " ")} {book.n_part > 1 ? `( ${t(`sad_granth.part`)} -  ${bookPart} )` : ""}
            </p>
            {/* share btn */}
            <div className="hidden">
              <ShareDownloadSection />
            </div>
          </div>
          <p
            className={cn("text-textColorPrimary text-[14px] my-2 lg:w-[90%] line-clamp-5 text-justify", mediumInter)}
            dangerouslySetInnerHTML={{ __html: book.desc || "" }}
          />

          {/* info card  */}
          <div className="mt-2 md:mt-8">
            {/* First row: Writer and Audio (mobile) */}
            <div
              className={cn(
                "w-full grid gap-2 my-2",
                (() => {
                  let count = 0;
                  if (writer) count++;
                  if (audio) count++;

                  if (count === 1) return "grid-cols-1";
                  return "grid-cols-1 md:grid-cols-2";
                })(),
              )}
            >
              {writer && <InfoCard label={`${t(`sad_granth.author.title`)}`} value={writer} fallback={`${t(`sad_granth.author.no_author`)}`} />}
              {audio ? (
                <div className="xs:block md:hidden">
                  <InfoCard label={`${t(`sad_granth.audio_by.title`)}`} value={audio} fallback={`${t(`sad_granth.audio_by.no_audio_by`)}`} />
                </div>
              ) : null}
            </div>

            {/* Second row: Category, Audio (desktop), Total Pages, Book Parts - 3 columns */}
            <div className="grid gap-2 my-2 grid-cols-1 xs:grid-cols-2 md:grid-cols-3">
              {book.type && (
                <InfoCard
                  label={`${t(`sad_granth.category.title`)}`}
                  value={book.type || `${t(`sad_granth.category.no_category`)}`}
                  fallback="No Publisher"
                />
              )}
              {audio ? (
                <div className="hidden md:block">
                  <InfoCard label={`${t(`sad_granth.audio_by.title`)}`} value={audio} fallback={`${t(`sad_granth.audio_by.no_audio_by`)}`} />
                </div>
              ) : null}

              {book_part_data?.total_pages && (
                <InfoCard
                  label={`${t(`sad_granth.total_pages.title`)}`}
                  value={String(book_part_data.total_pages)}
                  fallback={`${t(`sad_granth.total_pages.no_total_pages`)}`}
                />
              )}

              {book?.n_part && (
                <InfoCard
                  label={`${t(`sad_granth.book_parts.title`)}`}
                  value={`${t(`sad_granth.parts`, { count: book.n_part })}`}
                  fallback={`${t(`sad_granth.book_parts.no_book_parts`)}`}
                />
              )}
            </div>
          </div>

          <div>
            <p className={cn("md:hidden text-textColor my-4 text-center text-xl md:text-4xl first-letter-cap line-clamp-2", semiBold)}>
              {book.name} {book.n_part > 1 ? `( ${t(`sad_granth.part`)} -  ${bookPart} )` : ""}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:mt-10">
              <Buttons
                label={t(`sad_granth.download`)}
                isLoading={isLoading}
                icon={<FiDownload />}
                border="border border-text border-textHeadingColor"
                bgColor="hover:bg-cardBg"
                textColor="text-textHeadingColor"
                onClick={(e) => handleDownloadFile(e, book_part_data.pdf_path, book.n_part > 1 ? `${book.name}_Part-${bookPart}` : book.name)}
              />

              <Buttons
                label={t(`sad_granth.read_book`)}
                icon={<HiOutlineBookOpen size={18} />}
                bgColor="bg-textHeadingColor hover:bg-dailyDarshanChipColor"
                textColor="text-white"
                onClick={() => window.open(book_part_data.pdf_path, "_blank", "noopener,noreferrer")}
              />
            </div>

            {book?.has_audio_book ? (
              <div className="gap-2 mt-5 md:mt-10 flex flex-col-reverse md:flex-col justify-center items-center text-">
                <p className={cn("text-16 text-textColor xs:text-center", medium)}>{t("sad_granth.listen_audio_text")}</p>
                <div className="w-full md:w-1/2">
                  <Buttons
                    label={t("sad_granth.listen_audio_button_text")}
                    icon={<MdHeadset size={18} />}
                    bgColor="w-full bg-textColorPrimary hover:bg-titlePreviewColor"
                    textColor="text-white"
                    onClick={() => handlePlayAudio(book_part_data.playlist_id)}
                    isLoading={isAudioNavigateLoading}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="pt-5 space-y-3 md:mt-5">
        {/* md:text-4xl  MokuBrush */}
        <p className={cn("text-textColor text-xl md:text-2xl capitalize", medium)}>
          {book_part.length > 1
            ? `${t(`sad_granth.more_parts`, { book_name: book.name })}`
            : `${t(`sad_granth.explore_more_book`, { book_type: book.type, of: book.type ? ` ${t(`sad_granth.of`)}` : "" })}`}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 overflow-x-auto">
          {book_part?.length > 1
            ? book_part.map((item, index) => (
                <React.Fragment key={index}>
                  <DisplayBookCard
                    name={`${t(`sad_granth.part`)} - ${item.part_number}`}
                    url={
                      item.img
                        ? {
                            small: item.img.s.url,
                            medium: item.img.m.url,
                            large: item.img.l.url,
                            hd: item.img.hd.url,
                            original: item.img.og.url,
                          }
                        : {
                            small: item.thumb_img || FALLBACK_IMAGE_URL,
                            large: item.thumb_img || FALLBACK_IMAGE_URL,
                            hd: item.thumb_img || FALLBACK_IMAGE_URL,
                            original: item.thumb_img || FALLBACK_IMAGE_URL,
                            medium: item.thumb_img || FALLBACK_IMAGE_URL,
                          }
                    }
                    pdf_path={item.pdf_path}
                    img_aspect={item.img_aspect}
                    hasAudio={book.has_audio_book ? true : false}
                    navigatePath={`/sad-granth/${book.url_slug}/part-${item.part_number}`}
                    downLoadFineName={book.n_part > 0 ? `${book.name}_Part-${item.part_number}` : book.name}
                    blur_hash={item.img?.bh}
                  />
                </React.Fragment>
              ))
            : sadGranthData?.map((book, index) => {
                return (
                  <React.Fragment key={index}>
                    <DisplayBookCard
                      name={book.name.replaceAll("-", " ")}
                      url={
                        book.img
                          ? {
                              small: book.img.s.url,
                              medium: book.img.m.url,
                              large: book.img.l.url,
                              hd: book.img.hd.url,
                              original: book.img.og.url,
                            }
                          : {
                              small: book.thumb_img || FALLBACK_IMAGE_URL,
                              large: book.thumb_img || FALLBACK_IMAGE_URL,
                              hd: book.thumb_img || FALLBACK_IMAGE_URL,
                              original: book.thumb_img || FALLBACK_IMAGE_URL,
                              medium: book.thumb_img || FALLBACK_IMAGE_URL,
                            }
                      }
                      pdf_path={book.pdf_path}
                      img_aspect={book.img_aspect}
                      isBookPart={book.n_part}
                      hasAudio={book.has_audio_book}
                      navigatePath={book.n_part > 1 ? `/sad-granth/${book.url_slug}/part-1` : `/sad-granth/${book.url_slug}`}
                      tag={book.tag}
                      downLoadFineName={book.name}
                      blur_hash={book.img?.bh}
                    />
                  </React.Fragment>
                );
              })}
        </div>
      </div>
    </div>
  );
};

const ShareDownloadSection = () => {
  return (
    <div className="flex-1 flex items-start justify-start gap-x-3 ">
      <button className="p-3 lg:p-4 rounded-full bg-cardBg hover:bg-[#f8e4d6] transition">
        <FaRegHeart className="text-textHeadingColor" size={20} />
      </button>
      <button className="p-3 lg:p-4 rounded-full  bg-cardBg hover:bg-[#f8e4d6] transition">
        <PiShareFat className="text-textHeadingColor" size={20} />
      </button>
    </div>
  );
};

const InfoCard = ({ label, value, fallback }: { label: string; value?: string; fallback: string }) => {
  const { medium, normalInter } = useFontStyle();
  return (
    <div className="w-full bg-cardBg p-2 md:p-4 rounded-lg ">
      <p className={cn("body_text_l tracking-wide text-textHeadingColor font-medium", medium)}>{label}</p>
      <p className={cn("body_text_m tracking-wide text-textColor font-inter capitalize", normalInter)}>{value || fallback}</p>
    </div>
  );
};

const Buttons = ({
  label,
  icon,
  onClick,
  border,
  bgColor,
  textColor,
  isLoading,
}: {
  label: string;
  icon: ReactNode;
  onClick: (e: any) => void;
  border?: string;
  bgColor?: string;
  textColor?: string;
  isLoading?: boolean;
}) => {
  const { mediumInter, normalInter } = useFontStyle();
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={cn(
        "flex items-center justify-center gap-2 px-2 py-2 md:px-6 md:py-3 rounded-full transition text-16 tracking-wide",
        normalInter,
        border,
        bgColor,
        textColor,
        isLoading ? "opacity-70 cursor-not-allowed bg-cardBg" : "",
      )}
    >
      {isLoading ? (
        <CircularProgress size={22} sx={{ color: "#d05e2d" }} />
      ) : (
        <>
          <span>{label}</span>
          <span>{icon}</span>
        </>
      )}
    </button>
  );
};

export default SadGranthBaseOnId;

export function ErrorBoundary() {
  return <NodataFound text={"No Data Found"} />;
}
